'use strict';

/* ────────────────────────────────────────
   PRICE UPLIFT TABLE — definitive version
   ─────────────────────────────────────
   Format: [low, high] per night in €, after seasonal factor.
   priceMod() is applied on top based on occupancy.

   High-end values are set conservatively (~17% below theoretical max)
   to keep the optimistic range realistic and defensible.

   Winter values are at full strength — a property with high winter
   occupancy can absorb price increases. The exception is handled
   via effectMultiplier when pool+hot_tub already exist.
──────────────────────────────────────── */
const UPLIFTS = {
  pool:        { summer: [100, 125], shoulder: [50,  60],  winter: [0,   0]   },
  hot_tub:     { summer: [40,  50],  shoulder: [53,  65],  winter: [80,  100] },
  jacuzzi_int: { summer: [40,  50],  shoulder: [53,  65],  winter: [80,  100] },
  sauna:       { summer: [30,  40],  shoulder: [40,  55],  winter: [60,  80]  },
  cold_plunge: { summer: [30,  38],  shoulder: [30,  38],  winter: [30,  40]  }
};

/* ────────────────────────────────────────
   PRODUCT MATRIX
   Returns array of 3 tier objects (null = tier not available)
──────────────────────────────────────── */
function getProductTiers(hasPool, hasHotTub, outdoor, indoor) {
  const hasOut = outdoor !== 'none';
  const hasIn  = indoor  !== 'none';
  if (!hasOut && !hasIn) return null;

  // No pool + outdoor available
  if (!hasPool && hasOut) {
    if (!hasHotTub) return [
      { items: ['pool'],                                    cost: [7500,  9000],  count: 1 },
      { items: ['pool','hot_tub'],                          cost: [14000, 17000], count: 2 },
      { items: ['pool','hot_tub','sauna','cold_plunge'],    cost: [26000, 32000], count: 4 }
    ];
    return [
      { items: ['pool'],                                    cost: [7500,  9000],  count: 1 },
      { items: ['pool','sauna'],                            cost: [13000, 16000], count: 2 },
      { items: ['pool','sauna','cold_plunge'],              cost: [19000, 24000], count: 3 }
    ];
  }

  // Has pool + outdoor + indoor
  if (hasPool && hasOut && hasIn) {
    if (!hasHotTub) return [
      { items: ['hot_tub'],                                 cost: [6000,  8000],  count: 1 },
      { items: ['hot_tub','sauna'],                         cost: [13000, 17000], count: 2 },
      { items: ['hot_tub','sauna','cold_plunge'],           cost: [20000, 25000], count: 3 }
    ];
    // Already has pool + hot_tub: property is well-equipped, reduced marginal impact
    return [
      { items: ['sauna'],                                   cost: [6000,  9000],  count: 1, effectMultiplier: 0.60 },
      { items: ['sauna','cold_plunge'],                     cost: [13000, 18000], count: 2, effectMultiplier: 0.60 },
      null
    ];
  }

  // Has pool + outdoor only
  if (hasPool && hasOut && !hasIn) {
    if (!hasHotTub) return [
      { items: ['hot_tub'],                                 cost: [6000,  8000],  count: 1 },
      { items: ['hot_tub','sauna'],                         cost: [13000, 17000], count: 2 },
      { items: ['hot_tub','sauna','cold_plunge'],           cost: [20000, 25000], count: 3 }
    ];
    return [
      { items: ['sauna'],                                   cost: [6000,  9000],  count: 1, effectMultiplier: 0.60 },
      { items: ['sauna','cold_plunge'],                     cost: [13000, 18000], count: 2, effectMultiplier: 0.60 },
      null
    ];
  }

  // Has pool + indoor only
  if (hasPool && !hasOut && hasIn) {
    if (!hasHotTub) return [
      { items: ['sauna'],                                   cost: [6000,  9000],  count: 1 },
      { items: ['sauna','cold_plunge'],                     cost: [13000, 18000], count: 2 },
      { items: ['sauna','cold_plunge','jacuzzi_int'],       cost: [20000, 27000], count: 3 }
    ];
    return [
      { items: ['sauna'],                                   cost: [6000,  9000],  count: 1, effectMultiplier: 0.60 },
      { items: ['sauna','cold_plunge'],                     cost: [13000, 18000], count: 2, effectMultiplier: 0.60 },
      null
    ];
  }

  // No pool + indoor only
  if (!hasPool && !hasOut && hasIn) {
    if (!hasHotTub) return [
      { items: ['jacuzzi_int'],                             cost: [6000,  8000],  count: 1 },
      { items: ['jacuzzi_int','sauna'],                     cost: [13000, 17000], count: 2 },
      { items: ['jacuzzi_int','sauna','cold_plunge'],       cost: [20000, 27000], count: 3 }
    ];
    return [
      { items: ['sauna'],                                   cost: [6000,  9000],  count: 1 },
      { items: ['sauna','cold_plunge'],                     cost: [13000, 18000], count: 2 },
      null
    ];
  }

  return null;
}

/* ────────────────────────────────────────
   SUM PRICE UPLIFTS FOR A TIER'S ITEMS
──────────────────────────────────────── */
function sumUplifts(items) {
  return items.reduce((acc, item) => {
    const u = UPLIFTS[item] || UPLIFTS.sauna;
    return {
      summer:   [acc.summer[0]   + u.summer[0],   acc.summer[1]   + u.summer[1]],
      shoulder: [acc.shoulder[0] + u.shoulder[0], acc.shoulder[1] + u.shoulder[1]],
      winter:   [acc.winter[0]   + u.winter[0],   acc.winter[1]   + u.winter[1]]
    };
  }, { summer:[0,0], shoulder:[0,0], winter:[0,0] });
}

/* ────────────────────────────────────────
   PRICE MODIFIER — based on occupancy + season
   ─────────────────────────────────────
   Logic: a price increase only makes sense when the market
   is already absorbing the property at a decent rate.
   If occupancy is low, the equipment should drive MORE BOOKINGS
   first — not a higher price on few bookings.

   Summer / Shoulder:
     ≥ 80% → full price uplift (market is strong, price can move)
     60–79% → half uplift (partial room to increase)
     < 60%  → no price uplift (fill the calendar first)

   Winter (lower thresholds — wellness specifically drives off-season):
     ≥ 50% → full price uplift
     30–49% → half uplift
     < 30%  → no price uplift, occupancy uplift only
──────────────────────────────────────── */
function priceMod(occ, season) {
  if (season === 'winter') {
    if (occ >= 0.50) return 1.0;
    if (occ >= 0.30) return 0.5;
    return 0.0;
  }
  // summer and shoulder
  if (occ >= 0.80) return 1.0;
  if (occ >= 0.60) return 0.5;
  return 0.0;
}

/* ────────────────────────────────────────
   NEW OCCUPANCY — per season
──────────────────────────────────────── */
function newOcc(season, curr, hasPool, hasNewPool, pct) {
  const HARD_CAP = 0.95;
  if (season === 'summer') {
    if (curr >= 0.90) return curr; // price only
    if (hasPool && !hasNewPool) return curr; // pool already there
    return Math.min(HARD_CAP, curr + pct);
  }
  if (season === 'shoulder') {
    return Math.min(HARD_CAP, curr + pct);
  }
  if (season === 'winter') {
    const minUplift = 0.10;
    const available = HARD_CAP - curr;
    const guaranteed = Math.min(minUplift, available);
    const desired    = Math.min(pct, available);
    return Math.min(HARD_CAP, curr + Math.max(guaranteed, desired));
  }
  return curr;
}

/* ────────────────────────────────────────
   ROI DISPLAY STRING
   Always shows real approximation with "circa"
──────────────────────────────────────── */
function roiDisplay(investLow, investHigh, incLow, incHigh) {
  if (incHigh <= 0) return 'variabile';
  const best  = investLow  / (incHigh / 12);
  const worst = investHigh / (Math.max(incLow, 100) / 12);
  // Clamp to realistic display range: 5–12 months
  // Even if math gives faster payback, showing < 5 months reduces credibility
  const lo = Math.max(5,  Math.round(best));
  const hi = Math.min(12, Math.round(worst));
  if (lo >= hi) return `circa ${lo} mesi *`;
  return `circa ${lo}–${hi} mesi *`;
}

/* ────────────────────────────────────────
   MAIN CALCULATION
──────────────────────────────────────── */
function calculate(data) {
  const { hasPool, hasHotTub, outdoor, indoor,
          pS, oS, pSh, oSh, pW, oW } = data;

  const OS  = oS  / 100;
  const OSh = oSh / 100;
  const OW  = oW  / 100;

  const baseline = Math.round(
    (pS * OS * 90) + (pSh * OSh * 91) + (pW * OW * 184)
  );

  const tiers = getProductTiers(hasPool, hasHotTub, outdoor, indoor);

  if (!tiers) return { baseline, noSpace: true };

  const PCTS = [0.07, 0.14, 0.17]; // T1: 7pp (conservative single-equipment), T2: 14pp, T3: 17pp
  const TIER_NAMES = ['Pacchetto Essenziale', 'Pacchetto Intermedio', 'Pacchetto Completo'];

  const scenarios = tiers.map((tier, idx) => {
    if (!tier) return null;
    const effMult = tier.effectMultiplier || 1.0;
    const pct     = PCTS[idx];
    const u_raw   = sumUplifts(tier.items);

    // Apply effectMultiplier (reduced impact for already well-equipped properties)
    const u = {
      summer:   [u_raw.summer[0]   * effMult, u_raw.summer[1]   * effMult],
      shoulder: [u_raw.shoulder[0] * effMult, u_raw.shoulder[1] * effMult],
      winter:   [u_raw.winter[0]   * effMult, u_raw.winter[1]   * effMult]
    };
    const adjPct = pct * effMult; // occupancy uplift also reduced for well-equipped

    const hNP = tier.items.includes('pool');

    const mS  = priceMod(OS,  'summer');
    const mSh = priceMod(OSh, 'shoulder');
    const mW  = priceMod(OW,  'winter');

    // ── Price cap for low-occupancy seasons ──
    // When a season's occupancy is below 80%, the market hasn't yet demonstrated
    // willingness to pay a large premium — cap the price increase accordingly.
    // Above 80%, no cap: the market is absorbing current price, so a premium is defensible.
    // Caps scale with tier to reflect more amenities = larger justified premium.
    // Format: [low cap %, high cap %] of base price
    const TIER_CAPS = [[0.08, 0.12], [0.12, 0.18], [0.18, 0.25]];
    const [capLoPct, capHiPct] = TIER_CAPS[idx];

    function applyPriceCap(rawLo, rawHi, basePrice, occ) {
      if (occ >= 0.80) return [rawLo, rawHi]; // above 80% — no cap
      const capLo = basePrice * capLoPct;
      const capHi = basePrice * capHiPct;
      return [Math.min(rawLo, capLo), Math.min(rawHi, capHi)];
    }

    const [upliftSlo, upliftShi]   = applyPriceCap(u.summer[0]   * mS,  u.summer[1]   * mS,  pS,  OS);
    const [upliftShlo, upliftShhi] = applyPriceCap(u.shoulder[0] * mSh, u.shoulder[1] * mSh, pSh, OSh);
    const [upliftWlo, upliftWhi]   = applyPriceCap(u.winter[0]   * mW,  u.winter[1]   * mW,  pW,  OW);

    const newPS_lo  = pS  + upliftSlo;
    const newPS_hi  = pS  + upliftShi;
    const newPSh_lo = pSh + upliftShlo;
    const newPSh_hi = pSh + upliftShhi;
    const newPW_lo  = pW  + upliftWlo;
    const newPW_hi  = pW  + upliftWhi;

    const nOS  = newOcc('summer',   OS,  hasPool, hNP, adjPct);
    const nOSh = newOcc('shoulder', OSh, hasPool, hNP, adjPct);
    const nOW  = newOcc('winter',   OW,  hasPool, hNP, adjPct);

    const revLo = Math.round((newPS_lo*nOS*90) + (newPSh_lo*nOSh*91) + (newPW_lo*nOW*184));
    const revHi = Math.round((newPS_hi*nOS*90) + (newPSh_hi*nOSh*91) + (newPW_hi*nOW*184));

    const incLo = Math.max(0, revLo - baseline);
    const incHiRaw = Math.max(0, revHi - baseline);

    // Cap the optimistic high end at 20/30/40% of baseline per tier
    // Prevents implausibly large increases on high-value properties
    // Low end stays uncapped — preserves a meaningful range
    const BASELINE_CAPS = [0.20, 0.30, 0.40];
    const incHi = Math.min(incHiRaw, Math.round(baseline * BASELINE_CAPS[idx]));

    // Price deltas (midpoint of capped values, for description display)
    const dS  = Math.round((upliftSlo  + upliftShi)  / 2);
    const dSh = Math.round((upliftShlo + upliftShhi) / 2);
    const dW  = Math.round((upliftWlo  + upliftWhi)  / 2);

    // Occupancy deltas
    const dOS  = Math.round((nOS  - OS)  * 100);
    const dOSh = Math.round((nOSh - OSh) * 100);
    const dOW  = Math.round((nOW  - OW)  * 100);

    // ── Price vs. occupancy contribution split ──
    // Using midpoint prices for a single representative estimate
    const avgPS  = (newPS_lo  + newPS_hi)  / 2;
    const avgPSh = (newPSh_lo + newPSh_hi) / 2;
    const avgPW  = (newPW_lo  + newPW_hi)  / 2;

    const priceContrib = ((avgPS  - pS)  * nOS  * 90)
                       + ((avgPSh - pSh) * nOSh * 91)
                       + ((avgPW  - pW)  * nOW  * 184);
    const occContrib   = (pS  * (nOS  - OS)  * 90)
                       + (pSh * (nOSh - OSh) * 91)
                       + (pW  * (nOW  - OW)  * 184);
    const totalContrib = priceContrib + occContrib;
    const priceSharePct = totalContrib > 0 ? Math.round(priceContrib / totalContrib * 100) : 0;
    const occSharePct   = 100 - priceSharePct;

    // Add-on service (triggered by INPUT occ thresholds)
    const addS  = OS  >= 0.95;
    const addSh = OSh >= 0.95;
    const addW  = OW  >= 0.70;
    let addOnRev = 0;
    if (addS)  addOnRev += Math.round((90  * OS  / 5) * 350);
    if (addSh) addOnRev += Math.round((91  * OSh / 4) * 350);
    if (addW)  addOnRev += Math.round((184 * OW  / 3) * 350);

    return {
      tierIdx: idx,
      name: TIER_NAMES[idx],
      count: tier.count,
      cost: tier.cost,
      items: tier.items,
      incRange: [incLo, incHi],
      roi: roiDisplay(tier.cost[0], tier.cost[1], incLo, incHi),
      dS, dSh, dW, dOS, dOSh, dOW,
      priceSharePct, occSharePct,
      addOnRev,
      addOnTriggered: addOnRev > 0
    };
  }).filter(Boolean);

  // Global add-on (computed from Tier 3 or highest available)
  const top = scenarios[scenarios.length - 1];
  const globalAddOn = top ? top.addOnRev : 0;
  const addOnTriggered = globalAddOn > 0;

  return { baseline, scenarios, addOnRev: globalAddOn, addOnTriggered };
}

/* ────────────────────────────────────────
   GENERATE SCENARIO DESCRIPTION
   ─────────────────────────────────────
   Explains what drives the result in each season,
   including the reasoning when a price increase is
   not possible due to low occupancy.
──────────────────────────────────────── */
function scenarioDesc(s, data) {
  const { OS, OSh, OW } = data;
  const modS  = priceMod(OS,  'summer');
  const modSh = priceMod(OSh, 'shoulder');
  const modW  = priceMod(OW,  'winter');
  const pctS  = Math.round(OS  * 100);
  const pctSh = Math.round(OSh * 100);
  const pctW  = Math.round(OW  * 100);
  const lines = [];

  // ── Estate ──
  if (OS >= 0.90) {
    const priceNote = s.dS > 0
      ? `L'investimento permette un incremento del prezzo di circa ${s.dS} €/notte.`
      : `Il posizionamento di prezzo può consolidarsi, ma non stimiamo margine significativo con questo livello di investimento.`;
    lines.push(`In estate, l'occupazione (${pctS}%) è già al massimo raggiungibile — non stimiamo crescita ulteriore sulle notti prenotate. ${priceNote}`);
  } else if (modS === 0) {
    lines.push(`In estate, l'occupazione attuale (${pctS}%) non supporta ancora un aumento del prezzo — l'investimento agisce sull'occupazione, con un incremento stimato di circa ${s.dOS}%.`);
  } else if (s.dOS > 0 && s.dS > 0) {
    const priceNote = modS < 1.0
      ? `un incremento parziale del prezzo di circa ${s.dS} €/notte (effetto ridotto: con un'occupazione del ${pctS}%, applichiamo metà del potenziale massimo)`
      : `un incremento del prezzo di circa ${s.dS} €/notte`;
    lines.push(`In estate, stimiamo un aumento dell'occupazione di circa ${s.dOS}% e ${priceNote}.`);
  } else if (s.dS > 0) {
    const priceNote = modS < 1.0
      ? `un incremento parziale del prezzo di circa ${s.dS} €/notte — con un'occupazione del ${pctS}% applichiamo metà del potenziale massimo`
      : `un incremento del prezzo di circa ${s.dS} €/notte`;
    lines.push(`In estate, stimiamo ${priceNote}.`);
  } else {
    lines.push(`In estate, l'impatto dipende dalla saturazione del mercato nella tua zona.`);
  }

  // ── Spalla ──
  if (modSh === 0) {
    lines.push(`In stagione di spalla, l'occupazione attuale (${pctSh}%) non è ancora sufficiente per supportare un aumento del prezzo — l'investimento genera un incremento dell'occupazione di circa ${s.dOSh}%.`);
  } else {
    const parts = [];
    if (s.dSh > 0) {
      const priceNote = modSh < 1.0
        ? `un incremento parziale del prezzo di circa ${s.dSh} €/notte (occupazione ${pctSh}% → effetto dimezzato)`
        : `un incremento del prezzo di circa ${s.dSh} €/notte`;
      parts.push(priceNote);
    }
    if (s.dOSh > 0) parts.push(`un aumento dell'occupazione di circa ${s.dOSh}%`);
    if (parts.length) lines.push(`In stagione di spalla, stimiamo ${parts.join(' e ')}.`);
  }

  // ── Inverno ──
  if (modW === 0) {
    lines.push(`In bassa stagione, l'occupazione attuale (${pctW}%) è troppo bassa per giustificare un incremento del prezzo — l'investimento agisce esclusivamente sull'occupazione, con un incremento stimato di circa ${s.dOW}%. Portare il riempimento invernale a livelli stabili è il primo obiettivo.`);
  } else if (modW === 0.5) {
    const parts = [];
    if (s.dW  > 0) parts.push(`un incremento parziale del prezzo di circa ${s.dW} €/notte`);
    if (s.dOW > 0) parts.push(`un aumento dell'occupazione di circa ${s.dOW}%`);
    lines.push(`In bassa stagione (${pctW}% di occupazione attuale), c'è margine sia su prezzo che su occupazione: stimiamo ${parts.join(' e ')}.`);
  } else {
    const parts = [];
    if (s.dW  > 0) parts.push(`un incremento del prezzo di circa ${s.dW} €/notte`);
    if (s.dOW > 0) parts.push(`un aumento dell'occupazione di circa ${s.dOW}%`);
    lines.push(`In bassa stagione, dove risiede spesso il maggiore potenziale inespresso, stimiamo ${parts.join(' e ')}.`);
  }

  // ── Closing sentence: revenue range + price/occ split ──
  // Percentages rounded to nearest 5% so they always sum to 100
  const priceRnd = Math.round(s.priceSharePct / 5) * 5;
  const occRnd   = 100 - priceRnd;

  let splitNote;
  if (priceRnd <= 0) {
    splitNote = `interamente derivante da un aumento del tasso di riempimento`;
  } else if (priceRnd >= 100) {
    splitNote = `interamente derivante da un incremento del prezzo per notte`;
  } else {
    splitNote = `di cui circa ${priceRnd}% derivante da un incremento del prezzo per notte e circa ${occRnd}% da un aumento del tasso di riempimento`;
  }

  lines.push(`Di conseguenza, l'incremento di ricavo annuale stimato è compreso tra ${fmtEur(s.incRange[0])} e ${fmtEur(s.incRange[1])}, ${splitNote}.`);

  return lines.join(' ');
}

/* ────────────────────────────────────────
   FORMAT CURRENCY
   Rounds to nearest 200 · € symbol AFTER · Italian locale
──────────────────────────────────────── */
function fmtEur(n) {
  const rounded = Math.round(n / 200) * 200;
  return rounded.toLocaleString('it-IT') + ' €';
}

/* ────────────────────────────────────────
   BUILD WHATSAPP SHARE MESSAGE
   Generates a readable summary of results
   to share with a partner / co-owner.
   WhatsApp renders *text* as bold.
──────────────────────────────────────── */

// Stores the last calculated result so shareOnWhatsApp()
// can access it on direct user gesture (avoids iOS blocking)
let _shareResult = null;

function buildShareMessage(result) {
  const lines = [];

  lines.push('*Simulazione Phoenix Private Wellness*');
  lines.push('');
  lines.push(`Ricavo attuale stimato: *${fmtEur(result.baseline)}/anno*`);
  lines.push('');

  if (result.scenarios && result.scenarios.length) {
    result.scenarios.forEach((s, i) => {
      const ltr = String.fromCharCode(65 + i);
      const noun = s.count === 1 ? 'attrezzatura' : 'attrezzature';
      lines.push(`*Scenario ${ltr} — ${s.count} ${noun}*`);
      lines.push(`Investimento: ${fmtEur(s.cost[0])} – ${fmtEur(s.cost[1])}`);
      lines.push(`Incremento ricavo: ${fmtEur(s.incRange[0])} – ${fmtEur(s.incRange[1])}/anno`);
      lines.push(`ROI stimato: ${s.roi}`);
      lines.push('');
    });
  }

  lines.push('_Stima indicativa basata su benchmark di mercato italiano._');
  lines.push('');
  lines.push('👉 Fai anche tu la simulazione:');
  lines.push('https://phoenixprivatewellness.com/pages/simulatore-wellness');

  return lines.join('\n');
}

// Called directly on button click — window.open inside a user
// gesture is never blocked by iOS Safari, unlike a programmatic href
function shareOnWhatsApp() {
  if (!_shareResult) return;
  const msg = buildShareMessage(_shareResult);
  const url = 'https://wa.me/?text=' + encodeURIComponent(msg);
  window.open(url, '_blank');
}

/* ────────────────────────────────────────
   EQUIPMENT CATEGORY LABEL
   Shows the type of equipment (hot/cold/outdoor)
   without revealing specific model or brand.
   Gives enough to create curiosity, withholds
   enough to require the consultation.
──────────────────────────────────────── */
function getEquipmentLabel(items) {
  const HOT     = ['hot_tub', 'jacuzzi_int', 'sauna'];
  const COLD    = ['cold_plunge'];
  const OUTDOOR = ['pool'];

  const parts = [];
  const hotItems = items.filter(i => HOT.includes(i));
  const coldItems = items.filter(i => COLD.includes(i));
  const outdoorItems = items.filter(i => OUTDOOR.includes(i));

  if (outdoorItems.length > 0) parts.push('outdoor (piscina)');
  if (hotItems.length > 0) {
    const names = { hot_tub: 'jacuzzi / hot tub', jacuzzi_int: 'jacuzzi indoor', sauna: 'sauna' };
    const label = hotItems.map(i => names[i]).join(' o ');
    parts.push(`calda (${label})`);
  }
  if (coldItems.length > 0) parts.push('fredda (cold plunge)');

  return parts.join(' + ');
}

function getEquipmentWhy(items) {
  const hasPool  = items.includes('pool');
  const hasCold  = items.includes('cold_plunge');
  const hasHot   = ['hot_tub','jacuzzi_int','sauna'].some(i => items.includes(i));

  if (hasPool && !hasCold && !hasHot)
    return "La piscina agisce principalmente sull'occupazione e sul prezzo in alta stagione.";
  if (hasCold && hasHot)
    return "La combinazione di attrezzature calde e fredde massimizza l'attrattività in tutte le stagioni.";
  if (hasCold)
    return "Il cold plunge si utilizza tutto l'anno e viene sempre più ricercato dagli ospiti attenti al benessere.";
  if (hasHot)
    return "Le attrezzature calde valorizzano la proprietà in bassa stagione, quando gli spazi outdoor vengono meno utilizzati.";
  return '';
}

/* ────────────────────────────────────────
   RENDER RESULTS
──────────────────────────────────────── */
function renderResults(result, data) {
  const resSection = document.getElementById('results-section');
  resSection.style.display = 'block';

  // Scroll to results
  setTimeout(() => {
    resSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, 200);

  // Baseline
  document.getElementById('baseline-value').textContent = fmtEur(result.baseline);

  if (result.noSpace) {
    document.getElementById('no-space-msg').style.display = 'flex';
    document.getElementById('baseline-card').style.display = 'flex';
    return;
  }

  document.getElementById('baseline-card').style.display = 'flex';
  document.getElementById('results-disclaimer').style.display = 'block';
  document.getElementById('results-cta').style.display = 'block';
  document.getElementById('direct-contact-section').style.display = 'block';

  // Store result for share button — shareOnWhatsApp() reads this on click
  _shareResult = result;
  document.getElementById('wa-share-section').style.display = 'flex';

  // Scenarios
  const grid = document.getElementById('scenarios-grid');
  grid.innerHTML = '';

  const DS = { OS: data.oS/100, OSh: data.oSh/100, OW: data.oW/100 };

  result.scenarios.forEach((s, i) => {
    const isBest = (i === result.scenarios.length - 1);
    const card = document.createElement('div');
    card.className = 'scenario-card' + (isBest ? ' featured' : '');

    const investLo = fmtEur(s.cost[0]);
    const investHi = fmtEur(s.cost[1]);

    card.innerHTML = `
      <p class="scenario-badge">Scenario ${String.fromCharCode(65+i)}</p>
      <h3>${s.name}</h3>
      <p class="scenario-count">${s.count} attrezzatur${s.count===1?'a':'e'} inclus${s.count===1?'a':'e'}</p>
      <p class="scenario-equip-label">${getEquipmentLabel(s.items)}</p>
      <p class="scenario-equip-why">${getEquipmentWhy(s.items)}</p>
      <div class="scenario-divider"></div>
      <div class="metric-row">
        <span class="metric-label">Investimento</span>
        <span class="metric-value">${investLo} – ${investHi}</span>
      </div>
      <div class="metric-row">
        <span class="metric-label">Incremento ricavo / anno</span>
        <span class="metric-value increase">${fmtEur(s.incRange[0])} – ${fmtEur(s.incRange[1])}</span>
      </div>
      <div class="metric-row">
        <span class="metric-label">ROI stimato</span>
        <span class="metric-value gold">${s.roi}</span>
      </div>
      <p class="scenario-desc">${scenarioDesc(s, DS)}</p>
    `;
    grid.appendChild(card);
  });

  // Add-on service
  if (result.addOnTriggered) {
    const addCard = document.getElementById('addon-card');
    document.getElementById('addon-value').textContent = `${fmtEur(result.addOnRev)} / anno`;
    document.getElementById('addon-desc').textContent =
      'Il tuo tasso di riempimento è già alto in una o più stagioni. Puoi creare un servizio a pagamento — accesso alla zona wellness — addebitato separatamente per ogni soggiorno (stimato €350/soggiorno). Questo ricavo si aggiunge a quello del canone di locazione.';
    addCard.style.display = 'flex';
  }
}

/* ────────────────────────────────────────
   TEASER — shown above contact gate (Step 3)
   ─────────────────────────────────────
   Computes baseline and identifies which season
   has the most untapped potential, then renders
   a personalised teaser without revealing the
   actual investment scenarios.
──────────────────────────────────────── */
function computeTeaser(formData) {
  const { pS, oS, pSh, oSh, pW, oW } = formData;
  const OS  = oS  / 100;
  const OSh = oSh / 100;
  const OW  = oW  / 100;

  const baseline = Math.round(
    (pS * OS * 90) + (pSh * OSh * 91) + (pW * OW * 184)
  );

  // Revenue gap per season = money left on the table at current prices
  const gapS  = pS  * (1 - OS)  * 90;
  const gapSh = pSh * (1 - OSh) * 91;
  const gapW  = pW  * (1 - OW)  * 184;

  let bestSeason;
  if (gapW >= gapSh && gapW >= gapS)       bestSeason = 'bassa stagione';
  else if (gapSh >= gapW && gapSh >= gapS) bestSeason = 'stagione di spalla';
  else                                       bestSeason = 'alta stagione';

  // Intensity based on lowest occupancy across all seasons
  const minOcc = Math.min(oS, oSh, oW);
  const intensity = minOcc < 35 ? 'significativo'
                  : minOcc < 60 ? 'concreto'
                  : 'interessante';

  return { baseline, bestSeason, intensity };
}

function showTeaser(formData) {
  const block = document.getElementById('teaser-block');
  if (!block) return;
  const t = computeTeaser(formData);
  const intensityCap = t.intensity.charAt(0).toUpperCase() + t.intensity.slice(1);

  // Run full calculation — teaser reads from actual scenario output
  // so equipment hint is always consistent with what results will show
  const result = calculate(formData);
  let rangeHtml = '';

  if (!result.noSpace && result.scenarios && result.scenarios.length > 0) {
    const valid = result.scenarios.filter(Boolean);
    if (valid.length > 0) {
      const totalLow  = valid[0].incRange[0];
      const totalHigh = valid[valid.length - 1].incRange[1];

      // Derive equipment hint from actual Tier 1 recommendation — never contradicts results
      const tier1Label = getEquipmentLabel(valid[0].items);

      // Config-aware framing
      const hasPool = formData.hasPool;
      const hasOut  = formData.outdoor !== 'none';
      const hasIn   = formData.indoor  !== 'none';

      let configNote = '';
      if (hasPool && formData.hasHotTub) {
        configNote = 'per questa proprietà, ';
      } else if (hasPool) {
        configNote = 'per una villa con piscina, ';
      } else if (!hasPool && hasOut && hasIn) {
        configNote = 'per una proprietà con spazio interno ed esterno, ';
      } else if (!hasPool && hasOut) {
        configNote = 'per una proprietà con spazio esterno disponibile, ';
      } else if (!hasPool && hasIn) {
        configNote = 'per una proprietà con spazi interni disponibili, ';
      }

      const equipHint = `${configNote}il primo upgrade consigliato è di tipo ${tier1Label}`;

      rangeHtml = `
        <div class="teaser-range-row">
          <p class="teaser-metric-label">Incremento stimato</p>
          <p class="teaser-range-value">${fmtEur(totalLow)} – ${fmtEur(totalHigh)}<span>/anno</span></p>
        </div>
        <p class="teaser-equip">${equipHint}.</p>`;
    }
  }

  block.innerHTML = `
    <div class="teaser-block">
      <div class="teaser-grid">
        <div>
          <p class="teaser-metric-label">Ricavo annuale stimato attuale</p>
          <p class="teaser-metric-value">${fmtEur(t.baseline)}/anno</p>
        </div>
        <div>
          <p class="teaser-metric-label">Potenziale</p>
          <p class="teaser-metric-value gold">${intensityCap}</p>
        </div>
      </div>
      ${rangeHtml}
      <p class="teaser-desc">Inserisci i tuoi dati per accedere ai 3 scenari completi — investimento, incremento di ricavo e ROI stimato per la tua proprietà.</p>
      <p class="teaser-note">* Stima basata su benchmark di mercato per proprietà comparabili. Il potenziale reale della sua villa può essere diverso — per scoprirlo con precisione, prenoti una consulenza gratuita di 15 minuti con uno dei nostri esperti.</p>
    </div>
  `;
}

/* ────────────────────────────────────────
   FORM STATE
──────────────────────────────────────── */
const state = {
  step: 1
};

function getFormData() {
  return {
    rooms:     document.getElementById('rooms').value || 'N/D',
    hasPool:   document.querySelector('input[name="hasPool"]:checked').value === 'yes',
    hasHotTub: document.querySelector('input[name="hasHotTub"]:checked').value === 'yes',
    outdoor:   document.querySelector('input[name="outdoor"]:checked').value,
    indoor:    document.querySelector('input[name="indoor"]:checked').value,
    pS:  parseFloat(document.getElementById('priceSummer').value)   || 0,
    oS:  parseFloat(document.getElementById('occSummer').value)     || 0,
    pSh: parseFloat(document.getElementById('priceShoulder').value) || 0,
    oSh: parseFloat(document.getElementById('occShoulder').value)   || 0,
    pW:  parseFloat(document.getElementById('priceWinter').value)   || 0,
    oW:  parseFloat(document.getElementById('occWinter').value)     || 0
  };
}

/* ────────────────────────────────────────
   VALIDATION
──────────────────────────────────────── */
function validateStep1() {
  return true; // radio buttons always have a value
}

function validateStep2() {
  const d = getFormData();
  if (!d.pS || !d.pSh || !d.pW) {
    document.getElementById('err2').textContent = 'Inserisci un prezzo per notte in tutte e tre le stagioni.';
    document.getElementById('err2').style.display = 'block';
    return false;
  }
  document.getElementById('err2').style.display = 'none';
  return true;
}

function validateStep3() {
  const nome     = document.getElementById('c_nome_completo').value.trim();
  const email    = document.getElementById('c_email').value.trim();
  const telefono = document.getElementById('c_telefono').value.trim();
  if (!nome || !email || !telefono) {
    document.getElementById('err3').textContent = 'Compila tutti i campi per continuare.';
    document.getElementById('err3').style.display = 'block';
    return false;
  }
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  if (!emailOk) {
    document.getElementById('err3').textContent = 'Inserisci un indirizzo email valido.';
    document.getElementById('err3').style.display = 'block';
    return false;
  }
  document.getElementById('err3').style.display = 'none';
  return true;
}

/* ────────────────────────────────────────
   STEP TRACKING
   ─────────────────────────────────────
   Fires a silent Formspree ping each time a user
   advances to the next step — no contact data collected yet.
   Tells you exactly where people drop in the funnel.

   ⚠️ Create a separate Formspree endpoint for tracking:
      https://formspree.io/f/XXXXXXXX_TRACKING
   (Keep separate from main leads endpoint so your leads
    inbox doesn't get cluttered with analytics pings)
──────────────────────────────────────── */
function trackStep(stepNum, data) {
  const payload = new FormData();
  payload.append('_subject', `[Funnel] Simulatore — Step ${stepNum} completato`);
  payload.append('step_completato', stepNum);
  payload.append('timestamp', new Date().toISOString());
  payload.append('url', window.location.href);
  if (data) {
    Object.keys(data).forEach(k => payload.append(k, data[k]));
  }
  // Fire and forget — never blocks UX, silent on error
  fetch('https://formspree.io/f/xgobqvwv', {
    method: 'POST',
    body: payload,
    headers: { 'Accept': 'application/json' }
  }).catch(() => {});
}

/* ────────────────────────────────────────
   STEP NAVIGATION
──────────────────────────────────────── */
function goStep(n) {
  if (n === 2 && !validateStep1()) return;
  if (n === 3 && !validateStep2()) return;

  // ── Track when user reaches contact gate (Steps 1+2 data combined) ──
  if (n === 3) {
    trackStep(2, {
      camere:         document.getElementById('rooms').value || 'N/D',
      ha_piscina:     document.querySelector('input[name="hasPool"]:checked').value,
      ha_jacuzzi:     document.querySelector('input[name="hasHotTub"]:checked').value,
      spazio_esterno: document.querySelector('input[name="outdoor"]:checked').value,
      spazio_interno: document.querySelector('input[name="indoor"]:checked').value,
      prezzo_estate:  document.getElementById('priceSummer').value   + ' €',
      occ_estate:     document.getElementById('occSummer').value     + '%',
      prezzo_spalla:  document.getElementById('priceShoulder').value + ' €',
      occ_spalla:     document.getElementById('occShoulder').value   + '%',
      prezzo_inverno: document.getElementById('priceWinter').value   + ' €',
      occ_inverno:    document.getElementById('occWinter').value     + '%'
    });
    // Show personalised teaser above the contact gate
    showTeaser(getFormData());
  }

  document.getElementById(`step${state.step}`).classList.remove('active');
  document.getElementById(`step${n}`).classList.add('active');

  // Update progress
  ['1','2','3'].forEach(i => {
    const p = document.getElementById(`prog${i}`);
    const ni = parseInt(i);
    p.classList.remove('active','done');
    if (ni < n) p.classList.add('done');
    else if (ni === n) p.classList.add('active');
  });
  ['1','2'].forEach(i => {
    const l = document.getElementById(`line${i}`);
    l.classList.toggle('done', parseInt(i) < n);
  });

  if (n === 3) {
    document.querySelector('#prog2 .prog-circle').textContent = '✓';
  }

  state.step = n;
  const simTop = document.getElementById('progressBar') || document.querySelector('.simulator-wrap');
  if (simTop) simTop.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/* ────────────────────────────────────────
   SUBMIT & CALCULATE
   ─────────────────────────────────────
   Calculates results FIRST, then sends full lead profile
   (inputs + calculated scenarios) to Formspree so Stan has
   the complete picture before any follow-up call.
──────────────────────────────────────── */
async function submitAndCalculate() {
  if (!validateStep3()) return;

  const btn = document.getElementById('submitBtn');
  btn.textContent = 'Calcolo in corso…';
  btn.disabled = true;

  const formData = getFormData();
  const contact = {
    nome_completo: document.getElementById('c_nome_completo').value.trim(),
    email:         document.getElementById('c_email').value.trim(),
    telefono:      document.getElementById('c_telefono').value.trim()
  };

  // Calculate results first so we can include them in the payload
  const result = calculate(formData);

  // Build Formspree payload — full lead profile
  const payload = new FormData();
  payload.append('nome',      contact.nome_completo);
  payload.append('email',     contact.email);
  payload.append('telefono',  contact.telefono);
  payload.append('_subject',  `Lead simulatore — ${contact.nome_completo}`);
  payload.append('_source',   'simulatore_ppw');

  // Property inputs
  payload.append('camere',         formData.rooms);
  payload.append('ha_piscina',     formData.hasPool    ? 'Sì' : 'No');
  payload.append('ha_jacuzzi',     formData.hasHotTub  ? 'Sì' : 'No');
  payload.append('spazio_esterno', formData.outdoor);
  payload.append('spazio_interno', formData.indoor);
  payload.append('estate',   `€${formData.pS}/notte · ${formData.oS}% occupazione`);
  payload.append('spalla',   `€${formData.pSh}/notte · ${formData.oSh}% occupazione`);
  payload.append('inverno',  `€${formData.pW}/notte · ${formData.oW}% occupazione`);

  // Calculated output — so Stan knows exactly what this prospect saw
  payload.append('ricavo_attuale', fmtEur(result.baseline) + '/anno');
  if (!result.noSpace && result.scenarios) {
    result.scenarios.forEach((s, i) => {
      const ltr = String.fromCharCode(65 + i);
      payload.append(
        `scenario_${ltr}`,
        `${s.count} attrezzature · investimento ${fmtEur(s.cost[0])}–${fmtEur(s.cost[1])} · ` +
        `incremento ${fmtEur(s.incRange[0])}–${fmtEur(s.incRange[1])}/anno · ROI ${s.roi}`
      );
    });
  }

  // Submit to Formspree (fire and forget — never blocks results display)
  try {
    // ⚠️ SOSTITUISCI con il tuo endpoint Formspree
    fetch('https://formspree.io/f/xnjygwvj', {
      method: 'POST',
      body: payload,
      headers: { 'Accept': 'application/json' }
    });
  } catch(e) { /* silent */ }

  // Hide form, show results
  document.getElementById(`step${state.step}`).classList.remove('active');
  document.getElementById('progressBar').style.display = 'none';
  renderResults(result, formData);
}

/* ────────────────────────────────────────
   DIRECT CONTACT FORM SUBMIT
──────────────────────────────────────── */
async function submitDirectContact() {
  const nome     = document.getElementById('dc_nome').value.trim();
  const email    = document.getElementById('dc_email').value.trim();
  const telefono = document.getElementById('dc_telefono').value.trim();
  const messaggio = document.getElementById('dc_messaggio').value.trim();
  const errEl    = document.getElementById('dcError');
  const succEl   = document.getElementById('dcSuccess');
  const btn      = document.getElementById('dcSubmitBtn');

  if (!nome || !email) {
    errEl.textContent = 'Nome ed email sono obbligatori.';
    errEl.style.display = 'block';
    return;
  }
  errEl.style.display = 'none';
  btn.textContent = 'Invio…';
  btn.disabled = true;

  const payload = new FormData();
  payload.append('nome',      nome);
  payload.append('email',     email);
  payload.append('telefono',  telefono);
  payload.append('messaggio', messaggio);
  payload.append('_subject',  'Contatto diretto — Simulatore PPW');
  payload.append('_source',   'direct_contact_simulatore');

  try {
    // ⚠️ SOSTITUISCI con il tuo endpoint Formspree (può essere lo stesso del simulatore)
    const res = await fetch('https://formspree.io/f/xnjygwvj', {
      method: 'POST',
      body: payload,
      headers: { 'Accept': 'application/json' }
    });
    if (res.ok) {
      document.getElementById('dcFormGrid').querySelectorAll('input, textarea, button').forEach(el => {
        if (el.tagName !== 'BUTTON' || el.id !== 'dcSubmitBtn') return;
        el.style.display = 'none';
      });
      btn.style.display = 'none';
      succEl.style.display = 'block';
    } else {
      throw new Error();
    }
  } catch {
    btn.textContent = 'Invia Messaggio';
    btn.disabled = false;
    errEl.textContent = 'Errore nell\'invio. Scrivici direttamente a contact@phoenixprivatewellness.com';
    errEl.style.display = 'block';
  }
}

/* ────────────────────────────────────────
   INIT — update slider CSS gradient on load
──────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  ['occSummer','occShoulder','occWinter'].forEach(id => {
    const el = document.getElementById(id);
    function updateTrack() {
      const pct = ((el.value - el.min) / (el.max - el.min)) * 100;
      el.style.background = `linear-gradient(to right, var(--gold) 0%, var(--gold) ${pct}%, var(--bone) ${pct}%, var(--bone) 100%)`;
    }
    el.addEventListener('input', updateTrack);
    updateTrack();
  });
});